/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import { Link } from 'react-router-dom';
import Notification from '@tds/core-notification';
import Paragraph from '@tds/core-paragraph';
import Text from '@tds/core-text';
import UnorderedList from '@tds/core-unordered-list';

import { addLocale, replace } from '../misc';
import ErrorIcon from '../../assets/images/icon_error_messages.gif';
import LoginShema from './schema';
import request, { apiHost } from '../utils/request';
import ValidateLoader from '../UI/validateLoader';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import { Title } from './css';
import { CONSTANT } from '../utils/constant';
import { useLocaleContext } from '../context/LocaleContext';

import '../../assets/css/login.css';
import '../../assets/css/login_media_queries.css';

const Login = ({ history }) => {
  const [loading, setLoading] = useState(false);
  const [isError, setError] = useState(false);
  const isPasswordChangeRequired = request.getPasswordUpdate();
  const locale = useLocaleContext();
  const addLocaleToUrl = addLocale(locale);
  const localizedStrings = useLocalizedStringsContext();
  const {
    USERS: {
      USER_AUTHENTICATION: {
        labels: {
          TELUS_HEADING,
          USERNAME,
          PLACEHOLDER_EMAIL,
          USER_LOGIN_PASSWORD,
          USER_FORGOT_PASSWORD,
          USER_VERIFY_MESSAGE,
          CONTINUE,
          USER_LOGIN_BOTTOM,
          REGISTER_LABEL_LOGIN
        },
        ERRORS_MESSAGES: { GENERIC_ERROR_HEADING, GENERIC_ERROR_MESSAGE }
      }
    },
    '2fa': {
      loginMessage: {
        message1,
        message1_1,
        message2,
        message2_1,
        message2_2,
        message3,
        message4
      }
    }
  } = localizedStrings;
  const {
    KEYS: { ROLES }
  } = CONSTANT;

  const getRedirectUrl = role => {
    let redirectUrl = '/';
    switch (role) {
      case ROLES.SOV_ADMIN:
      case ROLES.SOV_AGENT: {
        redirectUrl = '/orders';
        break;
      }
      case ROLES.PRODUCT_TEAM: {
        redirectUrl = '/quote-approval';
        break;
      }
      default:
        break;
    }
    const valueMoveToDefects = localStorage.getItem('moveToDefects');
    if (
      valueMoveToDefects &&
      valueMoveToDefects !== null &&
      valueMoveToDefects !== 'null'
    ) {
      const { search } = JSON.parse(valueMoveToDefects) || {};
      redirectUrl = `/report-an-issue${search || ''}`;
      localStorage.removeItem('moveToDefects');
    }
    return redirectUrl;
  };

  const getUserProfile = () => {
    return request.get('/users/profile');
  };

  const clearUnRequiredLocalStorage = () => {
    const moveToDefectData = localStorage.getItem('moveToDefects');
    localStorage.clear();
    localStorage.setItem('moveToDefects', moveToDefectData);
  };

  useEffect(() => {
    const authenticate = async () => {
      const redirect = (changePassword, userProfile, url) => {
        if (JSON.parse(changePassword)) {
          history.push(addLocaleToUrl('/update-password'));
        } else if (url && url.length > 0) {
          try {
            const urlObj = JSON.parse(JSON.parse(url).url);
            history.push(
              addLocaleToUrl(
                `/${replace(urlObj.name)}/${replace(urlObj.formField.name)}/`
              )
            );
          } catch (err) {
            history.push(addLocaleToUrl(getRedirectUrl(userProfile.role.name)));
          }
        } else {
          history.push(addLocaleToUrl(getRedirectUrl(userProfile.role.name)));
        }

        // HACK to fix: header issue ideally
        // TODO need to remove this reload
        window.location.reload();
      };

      //  check if user is redirected from passport with token in params
      const queryData = new URLSearchParams(history.location.search);
      const token = queryData.get('token');
      const changePassword = JSON.parse(
        queryData.get('changePassword') || false
      );
      const url = decodeURIComponent(queryData.get('url') || '');

      // check if already logged in
      if (request.getToken() && request.getProfile())
        redirect(isPasswordChangeRequired, request.getProfile(), url);
      else if (token) {
        request.setToken(token);
        request.setPasswordUpdate(changePassword);
        try {
          const { data } = await getUserProfile();
          request.setProfile(data);
          request.setSSOLoginState(true);
          redirect(changePassword, data, url);
        } catch (err) {
          setError(true);
          localStorage.clear();
        }
      } else clearUnRequiredLocalStorage();
    };

    authenticate();
  }, [request.getToken()]);

  const loginRequest = values => {
    setLoading(true);
    const { email, password } = values;
    request
      .post('/users/login', { email, password })
      .then(response => {
        const { twoFactorAuthRequired } = response && response.data;
        /**
         * If 2FA is required
         */
        if (twoFactorAuthRequired) {
          history.push(addLocaleToUrl('/two-factor/verify'));
          localStorage.setItem('userEmail', email);
        } else {
          /**
           * If 2FA is not required
           */
          const { token, changePassword } = response && response.data;
          localStorage.setItem('isExpired', false);
          request.setToken(token);
          request.setPasswordUpdate(changePassword);
          getUserProfile().then(result => {
            const { data = {} } = result;
            request.setProfile(data);
            history.push(addLocaleToUrl(getRedirectUrl(data.role.name)));
            window.location.reload();
          });
        }
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  };

  return (
    <>
      <Formik
        initialValues={{
          email: '',
          password: ''
        }}
        validationSchema={LoginShema}
        onSubmit={(values, { resetForm }) => {
          loginRequest(values, resetForm);
        }}
      >
        {({ errors, touched }) => (
          <Form>
            <div className="layout-container">
              <div id="main">
                <div id="main-wrapper">
                  <div id="main-content" style={{ paddingTop: '0' }}>
                    <h2>
                      <div className="multifactorauthentication-notification-container">
                        <Notification variant="warning" copy="en">
                          <Paragraph>
                            {message1} <Text bold>{message1_1}</Text>
                          </Paragraph>
                          <Paragraph>
                            {message2} <Text bold>{message2_1}</Text>{' '}
                            {message2_2}
                            <UnorderedList>
                              <UnorderedList.Item>
                                {message3}
                              </UnorderedList.Item>
                            </UnorderedList>
                          </Paragraph>
                          <Paragraph>{message4}</Paragraph>
                        </Notification>
                      </div>
                      <div
                        id="main-content-title"
                        style={{ marginTop: '2rem' }}
                      >
                        <Title>{TELUS_HEADING}</Title>
                      </div>
                    </h2>

                    {isError ? (
                      <div
                        id="error-message-content"
                        style={{ display: 'block' }}
                      >
                        <div id="error-message-icon">
                          <img src={ErrorIcon} alt="error_icon" />
                        </div>
                        <div id="error-message-title">
                          <span className="frg-body-font-23px">
                            {GENERIC_ERROR_HEADING}
                          </span>
                        </div>
                        <div className="clear" />
                        <div id="error-message-message">
                          <span className="frg-body-font-14px">
                            {GENERIC_ERROR_MESSAGE}
                            <br />
                          </span>
                        </div>
                      </div>
                    ) : null}
                    <div
                      id="sso-content-container"
                      style={{ textAlign: 'center' }}
                    >
                      <span>
                        <button
                          type="button"
                          className="frg-button button-wide color-purple"
                          onClick={() =>
                            window.location.replace(
                              `${apiHost}/users/sso-login`
                            )
                          }
                          style={{ marginBottom: '10px' }}
                        >
                          Login With TELUS SSO
                        </button>
                      </span>
                      <span style={{}}>OR</span>
                    </div>

                    <div id="main-content-login">
                      <div
                        id="username_container"
                        style={{ position: 'relative', marginTop: '20px' }}
                      >
                        <div className="main-content-form-headers">
                          <span className="frg-body-font-14px">{USERNAME}</span>
                        </div>
                        <div
                          style={{ position: 'relative' }}
                          className={`input-container ${
                            errors.email && touched.email ? 'error' : ''
                          }`}
                        >
                          <Field
                            type="email"
                            name="email"
                            style={{ width: '100%' }}
                            className="select-style"
                            placeholder={PLACEHOLDER_EMAIL}
                          />

                          <>
                            <div className="error_messages">
                              <span className="frg-body-font-14px">
                                {errors.email}
                              </span>
                            </div>
                            <div
                              id="username_error_icon"
                              className="error_messages_icon"
                            >
                              <img src={ErrorIcon} alt="error_icon" />
                            </div>
                          </>
                        </div>
                      </div>
                      <div style={{ marginTop: 15, position: 'relative' }}>
                        <div className="main-content-form-headers">
                          <span className="frg-body-font-14px">
                            {USER_LOGIN_PASSWORD} (
                            <Link
                              to="/forgot-password"
                              style={{
                                fontSize: 12,
                                textDecorationLine: 'underline'
                              }}
                            >
                              {USER_FORGOT_PASSWORD}
                            </Link>
                            )
                          </span>
                        </div>
                        <div
                          style={{ position: 'relative' }}
                          className={`input-container ${
                            errors.password && touched.password ? 'error' : ''
                          }`}
                        >
                          <Field
                            type="password"
                            name="password"
                            style={{ width: '100%' }}
                            className="select-style"
                            placeholder={USER_LOGIN_PASSWORD}
                          />
                          <>
                            <div className="error_messages">
                              <span className="frg-body-font-14px">
                                {errors.password}
                              </span>
                            </div>
                            <div
                              id="username_error_icon"
                              className="error_messages_icon"
                            >
                              <img src={ErrorIcon} alt="error_icon" />
                            </div>
                          </>
                        </div>
                      </div>
                      {loading && (
                        <ValidateLoader message={USER_VERIFY_MESSAGE} />
                      )}
                      <div
                        style={{
                          marginTop: 20,
                          marginBottom: 20,
                          textAlign: 'center'
                        }}
                      >
                        <span>
                          <button
                            type="submit"
                            className="frg-button button-wide color-green"
                          >
                            {CONTINUE}
                          </button>
                        </span>
                      </div>
                    </div>
                    <div
                      id="main-content-register"
                      style={{ textAlign: 'center' }}
                    >
                      <div>
                        <span className="frg-body-font-17px">
                          {USER_LOGIN_BOTTOM}
                        </span>
                      </div>
                      <div>
                        <span className="frg-body-font-14px">
                          <Link
                            to="/registration"
                            style={{ textDecorationLine: 'underline' }}
                          >
                            {REGISTER_LABEL_LOGIN}
                          </Link>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

Login.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    location: PropTypes.shape({
      search: PropTypes.string.isRequired
    }).isRequired
  }).isRequired
};
export default Login;
